import { useCallback, useEffect, useState } from 'react';
import { apiService, ServerWhitelistActionDTO, ServerWhitelistDTO } from '@/services/api.service';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import settings from '@/pages/SettingsPage.module.scss';
import styles from './Whitelists.module.scss';

/** Pterodactyl : seul un serveur allumé reçoit une commande. */
function isRunning(server: ServerWhitelistDTO): boolean {
  return server.status?.toLowerCase() === 'running';
}

function serverState(server: ServerWhitelistDTO): string {
  switch (server.status?.toLowerCase()) {
    case 'running': return 'Allumé';
    case 'starting': return 'Démarrage';
    case 'stopping': return 'Arrêt';
    default: return 'Éteint';
  }
}

const UNREADABLE_HINT =
  "Le fichier de liste blanche de ce serveur n'est pas dans son volume (image à mettre à jour) : les boutons envoient la commande, sans pouvoir la vérifier.";

/**
 * Liste blanche de chaque serveur de jeu du groupe (/whitelist de ModerationMod) : allumée ou
 * éteinte, et ses noms. Les changements passent par la console du serveur (Pterodactyl), puis
 * l'état est relu dans le fichier du serveur.
 */
export function ServerWhitelists() {
  // Prime : NationsGlory seulement (jeu à groupes).
  const prime = useActiveGameGroup() !== null;
  // Lecture seule (<projet>.whitelist:read) : états et noms, sans bouton d'écriture.
  const writable = useAuthStore((s) => s.canWrite)('whitelist');
  const notify = useNotificationsStore.getState;
  const [servers, setServers] = useState<ServerWhitelistDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ server: ServerWhitelistDTO; enabled: boolean } | null>(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await apiService.getServerWhitelists();
    if (result.data && Array.isArray(result.data.servers)) {
      setServers(result.data.servers);
    } else {
      setServers([]);
      // Satellite d'avant 1.0.24 : /servers y est lu comme un joueur nommé « servers » (404).
      setError(result.status === 404
        ? 'Listes des serveurs indisponibles : le satellite est trop ancien (version 1.0.24 ou plus).'
        : result.error ?? 'Réponse inattendue');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Résultat d'une action : la ligne prend l'état relu, la notification dit si c'est vérifié. */
  const settle = (identifier: string, result: { data?: ServerWhitelistActionDTO; error?: string }) => {
    setBusy(null);
    if (result.data) {
      const updated = result.data.server;
      setServers((list) => list.map((s) => (s.identifier === identifier ? updated : s)));
      if (result.data.confirmed) notify().success(result.data.message);
      else notify().warning(result.data.message, 12000);
    } else {
      notify().error(result.error ?? 'Changement impossible.', 12000);
    }
  };

  const toggle = async (server: ServerWhitelistDTO, enabled: boolean) => {
    setConfirm(null);
    setBusy(server.identifier);
    settle(server.identifier, await apiService.setServerWhitelistStatus(server.identifier, enabled));
  };

  const addName = async (server: ServerWhitelistDTO) => {
    const name = newName.trim();
    if (!name) return;
    setBusy(server.identifier);
    const result = await apiService.addServerWhitelistName(server.identifier, name);
    if (result.data) setNewName('');
    settle(server.identifier, result);
  };

  const removeName = async (server: ServerWhitelistDTO, name: string) => {
    setBusy(server.identifier);
    settle(server.identifier, await apiService.removeServerWhitelistName(server.identifier, name));
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          Listes blanches des serveurs <GameGroupBadge />
        </div>
        <div className={styles.rowActions}>
          <span className={styles.sectionHint}>Commande en jeu : /whitelist, sur le serveur</span>
          <button className={styles.smallButton} onClick={load} disabled={loading}>Actualiser</button>
        </div>
      </div>

      {loading ? (
        <span className={styles.muted}>Lecture des serveurs...</span>
      ) : error ? (
        <div className={styles.card}>
          <span className={styles.muted}>{error}</span>
          <button className={styles.smallButton} onClick={load}>Réessayer</button>
        </div>
      ) : servers.length === 0 ? (
        <span className={styles.muted}>Aucun serveur dans ce groupe.</span>
      ) : (
        <div className={styles.table}>
          <div className={`${styles.row} ${styles.headRow}`}>
            <span>Serveur</span>
            <span>État</span>
            <span>Liste blanche</span>
            <span>Noms</span>
            <span />
          </div>
          {servers.map((server) => {
            const running = isRunning(server);
            const working = busy === server.identifier;
            const expanded = open === server.identifier;
            return (
              <div key={server.identifier}>
                <div className={styles.row}>
                  <span className={styles.serverName} title={server.identifier}>{server.name}</span>
                  <span className={styles.muted}>{serverState(server)}</span>
                  <span title={server.readable ? undefined : UNREADABLE_HINT}>
                    {!server.readable ? (
                      <span className={`${styles.pill} ${styles.pillUnknown}`}>Illisible</span>
                    ) : (
                      <span className={`${styles.pill} ${server.enabled ? styles.pillOn : styles.pillOff}`}>
                        {server.enabled ? 'Activée' : 'Désactivée'}
                      </span>
                    )}
                  </span>
                  <span className={styles.muted}>{server.readable ? server.names.length : '?'}</span>
                  <span className={styles.rowActions}>
                    {writable && (
                      <button
                        className={server.readable && server.enabled ? styles.toggleOff : styles.toggleOn}
                        disabled={!running || working}
                        title={running ? undefined : 'Serveur éteint : sa liste blanche ne change que serveur allumé.'}
                        onClick={() => setConfirm({ server, enabled: !(server.readable && server.enabled) })}
                      >
                        {working ? '...' : server.readable && server.enabled ? 'Désactiver' : 'Activer'}
                      </button>
                    )}
                    <button className={styles.smallButton} onClick={() => setOpen(expanded ? null : server.identifier)}>
                      {expanded ? 'Fermer' : 'Noms'}
                    </button>
                  </span>
                </div>
                {expanded && (
                  <div className={styles.details}>
                    {!server.readable && <span className={styles.notice}>{UNREADABLE_HINT}</span>}
                    {server.readable && server.names.length === 0 && (
                      <span className={styles.muted}>Aucun joueur inscrit sur ce serveur.</span>
                    )}
                    {server.names.length > 0 && (
                      <div className={styles.names}>
                        {server.names.map((name) => (
                          <span key={name} className={styles.nameChip}>
                            {name}
                            {writable && (
                              <button
                                className={styles.chipRemove}
                                disabled={!running || working}
                                title={`Retirer ${name}`}
                                onClick={() => removeName(server, name)}
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {writable && <div className={styles.addRow}>
                      <input
                        className={styles.input}
                        value={newName}
                        placeholder="Pseudo Recube ou gamertag du joueur..."
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && running && !working && addName(server)}
                      />
                      <button
                        className={styles.toggleOn}
                        disabled={!running || working || !newName.trim()}
                        onClick={() => addName(server)}
                      >
                        Ajouter
                      </button>
                      {!running && <span className={styles.muted}>Serveur éteint : rien ne peut changer.</span>}
                    </div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirm && (
        <div className={settings.modalOverlay} onClick={() => setConfirm(null)}>
          <div className={settings.modalSmall} onClick={(e) => e.stopPropagation()}>
            <h3 className={settings.deleteModalTitle}>
              {confirm.enabled ? 'Activer' : 'Désactiver'} la liste blanche de {confirm.server.name} ?
            </h3>
            <p className={settings.deleteModalDescription}>
              {confirm.enabled
                ? <>Les joueurs non inscrits connectés à <strong>{confirm.server.name}</strong> seront expulsés de ce serveur. Ceux qui ont le contournement{prime ? ' ou sont prime' : ''} restent.</>
                : <>Tout joueur pourra de nouveau rejoindre <strong>{confirm.server.name}</strong>.</>}
            </p>
            <div className={settings.modalActions}>
              <button className={settings.modalCancelButton} onClick={() => setConfirm(null)}>Annuler</button>
              <button
                className={confirm.enabled ? settings.modalConfirmButtonDanger : settings.modalConfirmButton}
                onClick={() => toggle(confirm.server, confirm.enabled)}
              >
                {confirm.enabled ? 'Activer' : 'Désactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
